import React, { useState } from "react";
import { X } from "lucide-react";

/**
 * BulletField — inline bullet-list editor.
 * Type text → press Enter (or Ctrl+Enter) to add a bullet.
 * Click a bullet to edit inline. Backspace on empty input removes last bullet.
 */
export default function BulletField({ values, onChange, placeholder = "Type and press Enter", testId }) {
  const [input, setInput] = useState("");
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingText, setEditingText] = useState("");

  const commit = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...(values || []), v]);
    setInput("");
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      // Enter or Ctrl+Enter both work; prevent default to avoid form submit
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !input && values.length) {
      // pop last bullet for quick correction
      onChange(values.slice(0, -1));
    }
  };

  const removeAt = (i) => onChange(values.filter((_, ix) => ix !== i));

  const startEdit = (i) => {
    setEditingIndex(i);
    setEditingText(values[i]);
  };
  const commitEdit = () => {
    if (editingIndex < 0) return;
    const t = editingText.trim();
    if (!t) removeAt(editingIndex);
    else onChange(values.map((v, i) => (i === editingIndex ? t : v)));
    setEditingIndex(-1);
    setEditingText("");
  };

  return (
    <div data-testid={testId}>
      {values.length > 0 && (
        <ul className="mb-2 space-y-1">
          {values.map((v, i) => (
            <li key={i} className="group flex items-start gap-2 rounded-lg px-2 py-1 hover:bg-[#F3F1EC]" data-testid={`${testId}-item-${i}`}>
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#0A4C36] shrink-0" />
              {editingIndex === i ? (
                <input
                  autoFocus
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitEdit(); } if (e.key === "Escape") { setEditingIndex(-1); } }}
                  className="flex-1 bg-transparent outline-none text-sm text-[#1F2A24]"
                />
              ) : (
                <button type="button" onClick={() => startEdit(i)} className="flex-1 text-left text-sm text-[#1F2A24] leading-relaxed break-words">{v}</button>
              )}
              <button type="button" onClick={() => removeAt(i)} className="opacity-0 group-hover:opacity-100 text-[#8A9992] hover:text-red-500" data-testid={`${testId}-remove-${i}`}>
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          className="input-clean pr-16"
          placeholder={placeholder}
          data-testid={`${testId}-input`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-[#8A9992] tracking-widest uppercase">Enter</span>
      </div>
    </div>
  );
}
